<template>
	<div class="page">
		<div class="background"></div>
		<v-container class="content ma-n4 ma-sm-n8 pa-0" fluid>

			<v-row class="flex-column flex-sm-row" no-gutters style="min-height: 100vh;">
				<v-col
						align-self="stretch"
						class="info-container"
						cols="12"
						sm="5"
				>
					<div class="align-center d-flex info-inner px-4 pa-sm-8">
						<v-row>
							<v-col class="d-flex flex-column white--text">
								<v-img
										:class="{
										'my-4': $vuetify.breakpoint.xsOnly,
										'ms-n3': $vuetify.breakpoint.xsOnly || $vuetify.breakpoint.mdAndUp,
										'ms-n2': $vuetify.breakpoint.smOnly,
										'align-self-center': $vuetify.breakpoint.xsOnly
									}"
										:max-width="logoWidth"
										src="/images/logo_bw_white.svg"
								></v-img>
								<div v-if="$vuetify.breakpoint.smAndUp">
									<h4
											class="font-weight-medium mb-2 mt-8"
											style="line-height: 1.2 !important"
									>
										{{ copy.heading }}
									</h4>
									<template v-for="point in copy.points">
										<div class="mt-12">
											<v-icon color="#ff876f" x-large>{{ point.icon }}</v-icon>
											<div class="font-weight-bold mt-2 text-body-1">
												{{ point.heading }}
											</div>
											<span
													class="opacity-70 text-body-2 white--text"
													style="line-height: 1.2 !important"
											>
												<template v-if="$vuetify.breakpoint.smAndDown">
													{{ point.text.short }}
												</template>
												<template v-else>
													{{ point.text.long }}
												</template>
											</span>
										</div>
									</template>
								</div>
							</v-col>
						</v-row>
					</div>
				</v-col>
				<v-col
						align-self="stretch"
						class="form-container"
						cols="12"
						sm="7"
				>
					<div class="align-center d-flex form-inner pa-4 pa-sm-8">
						<v-row>
							<v-col class="align-center d-flex flex-column">
								<h1 class="mt-4 mt-sm-0 text-center text-h2 text-sm-h1">
									Welcome{{ user.first_name ? `, ${user.first_name}` : null }}</h1>
								<p
										class="grey--text text--darken-1 mt-4 mt-sm-8 text-center"
										:class="$vuetify.breakpoint.lgAndUp ? 'text-body-1' : 'text-body-2'"
								>
									Complete your registration below to start building your first pass
									today!
								</p>

								<v-divider class="my-8 w-100"></v-divider>

								<v-row>
									<v-col>
										<v-form ref="form" v-model="formIsValid" style="min-width: 300px">
											<v-row>
												<v-col>
													<v-text-field
															v-model="user.first_name"
															autocomplete="off"
															class="rounded-lg required"
															hide-details="auto"
															label="First Name"
															outlined
															:rules="rules.firstName"
													></v-text-field>
												</v-col>
											</v-row>
											<v-row>
												<v-col>
													<v-text-field
															v-model="user.last_name"
															class="rounded-lg required"
															hide-details="auto"
															label="Last Name"
															outlined
															:rules="rules.lastName"
													></v-text-field>
												</v-col>
											</v-row>
											<v-row>
												<v-col>
													<v-text-field
															v-model="user.title"
															class="rounded-lg required"
															hide-details="auto"
															label="Job Title"
															outlined
															:rules="rules.title"
															type="email"
													></v-text-field>
												</v-col>
											</v-row>
											<v-row>
												<v-col>
													<v-text-field
															v-model="user.email"
															class="rounded-lg required"
															hide-details="auto"
															label="Email"
															outlined
															:rules="rules.email"
															type="email"
													></v-text-field>
												</v-col>
											</v-row>
											<v-row>
												<v-col>
													<vue-tel-input-vuetify
															ref="phone"
															v-model="user.phone"
															append-icon="mdi-menu-down"
															class="required rounded-lg"
															clearable
															:defaultCountry="countryCode"
															hide-details="auto"
															label="Phone"
															mode="international"
															outlined
															:preferred-countries="['us', 'ca']"
															:rules="rules.phone"
															@input="phoneInputEvent"
															@keydown="phoneKeyEvent"
													></vue-tel-input-vuetify>
												</v-col>
											</v-row>
											<v-row>
												<v-col>
													<v-text-field
															v-model="user.password"
															:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
															class="rounded-lg required"
															hide-details="auto"
															label="Password"
															outlined
															placeholder=""
															:rules="rules.password"
															:type="showPassword ? 'text' : 'password'"
															@click:append="showPassword = !showPassword"
													>
														<template v-slot:message="{ key, message }">
															<span :inner-html.prop="message"></span>
														</template>
													</v-text-field>
												</v-col>
											</v-row>
										</v-form>
									</v-col>
								</v-row>

								<v-progress-linear
										v-if="loading"
										class="my-8 w-100"
										color="secondary"
										height="2"
										indeterminate
								></v-progress-linear>
								<v-divider v-if="!loading" class="my-8 w-100"></v-divider>

								<v-row class="w-100" no-gutters>
									<v-col align-self="center">
									</v-col>
									<v-col cols="auto">
										<v-btn
												class="rounded-lg"
												color="primary"
												depressed
												:disabled="!formIsValid"
												:large="$vuetify.breakpoint.smAndDown"
												:loading="loading"
												:x-large="$vuetify.breakpoint.mdAndUp"
												@click.stop="onSubmit"
										>
											Create Account
											<v-icon class="me-n2 ms-2">mdi-chevron-right</v-icon>
										</v-btn>
									</v-col>
								</v-row>
							</v-col>
						</v-row>
					</div>
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script>
import $api from "@api/api";
import { helpers } from '@app/Shared/helpers';
import IronClad from '@services/IronClad/api';

export default {
	name: 'CreateAccount',
	props: {
		countryCode: {
			type: String,
			default: () => 'US'
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		user: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			copy: {
				heading: "Deliver Unique Experience Passes To Your Community",
				points: [
					{
						"heading": "Our Platform. Your Brand.",
						"text": {
							"short": "Bandwango’s fully white-labeled technology enables you to keep your brand front and center for customers.",
							"long": "At Bandwango LLC, we take pride in our white-labeled technology, ensuring your brand is prominently displayed and easily recognizable to valued customers. Build brand loyalty effortlessly, with cutting-edge solutions."
						},
						"icon": "mdi-bullhorn-outline"
					},
					{
						"heading": "Not An App, But Just As Easy.",
						"text":
								{
									"short": "Bandwango passes are mobile web applications delivered seamlessly via SMS and e-mail.",
									"long": "Discover the convenience of Bandwango passes! Our mobile web apps deliver a smooth user experience to customers' smartphones via SMS and e-mail. Embrace modern tech and engage your audience."
								},
						"icon": "mdi-handshake-outline"
					},
					{
						"heading": "Real People. Real Results.",
						"text": {
							"short": "Real ROI: Drive more customers through local doors with our comprehensive reporting and insights.",
							"long": "At Bandwango, we understand that your business's success relies on real results. Our platform delivers tangible ROI with increased foot traffic, tracked metrics, and data-driven decisions for optimal performance."
						},
						"icon": "mdi-chart-line"
					}
				]

			},
			loading: false,
			firstName: this.user?.name?.trim().split(' ')[0],
			formIsValid: false,
			lastName: this.user?.name?.trim().split(' ')[1],
			phoneIsValid: false,
			rules: {
				firstName: [v => !!v || 'First name is required.'],
				lastName: [v => !!v || 'Last name is required.'],
				password: [
					v => !!v || 'Password is required.',
					// v => v?.length >= 8 || 'Password must be at least 8 characters.',
					// v => v?.length <= 32 || 'Password must be 32 characters or less.',
					v => {
						const passes = {
							num: false,
							lower: false,
							upper: false,
							special: false,
							len: false
						};
						const patterns = {
							num: /\d+/,
							lower: /[a-z]+/,
							upper: /[A-Z]+/,
							special: /[*.!@$%^&(){}[\]:;<>,?/~_+\-=|]+/,
							len: /^.{8,32}$/
						};
						Object.keys(patterns).forEach(key => {
							passes[key] = patterns[key].test(v);
						});
						return (
								!Object.keys(passes).some(key => !passes[key]) ||
								`Password must contain: <ul class="mt-2 password-list"><li class="${
										passes.upper && 'success--text'
								}">One uppercase letter</li><li class="${
										passes.lower && 'success--text'
								}">One lowercase letter</li><li class="${
										passes.num && 'success--text'
								}">One number</li><li class="${
										passes.special && 'success--text'
								}">One special character</li><li class="${
										passes.len && 'success--text'
								}">8-32 characters</li></ul>`
						);
					}
				],
				phone: [
					v => !!v || 'Phone is required.',
					() => this.phoneIsValid || 'Please enter a valid phone.'
				],
				title: [v => !!v || 'Job title is required.'],
				userTos: [v => !!v && v === 'agreed' || 'Please agree to the terms']
			},
			showPassword: false,
			userTos: "",
			ironCladAgreement: window.location.href.includes('app.bandwango.com') ? 4 : 1
		};
	},
	computed: {
		logoWidth() {
			if(this.$vuetify.breakpoint.xsOnly) return 144; // height = 80
			if(this.$vuetify.breakpoint.mdAndUp) return 180;
			return 120;
		}
	},
	methods: {
		async save() {
			return new Promise(async resolve => {

				const user = helpers.pick(this.user, [
					'first_name',
					'last_name',
					'email',
					'phone',
					'password'
				]);
				user.password_confirmation = user.password;
				user.require_new_password = 0;

				const url = `/partner-portal/${this.partner.id}/users/complete-account/${this.user.id}`;

				axios
						.put(url, user)
						.then(() => {
							window.EventBus.$emit('system-messages', [
								{ message: 'User updated - redirecting now...', messageType: 'success' }
							]);
							setTimeout(() => {
								window.location.href = `/partner-portal/${this.partner.id}/`;
							}, 4000);
							resolve();
						})
						.catch(error => {
							this.loading = false;
							window.EventBus.$emit('system-messages', [
								{ message: error.message, messageType: 'error' }
							]);
							resolve();
						});
			});
		},
		onSubmit() {
			const isValid = this.$refs.form.validate();
			if(!isValid) return (this.formIsValid = false);

			this.loading = true;

			if(this.user.require_new_password) {
				// firstVisit = true;
				this.user.require_new_password = '0';
			}
			// TODO: should we add confirm password in the form? It is required for the api call
			this.user.password_confirmation = this.user.password;

			this.save();
		},
		goBack() {
			window.location.href = `/merchant-portal/${this.venue.id}/invites/view/submit-landing/${this.invite.id}?status=invited`
		},
		phoneInputEvent(formattedNumber, { valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			if(!valid) this.formIsValid = false;
			// For field validation rule
			this.phoneIsValid = valid;
		},
		phoneKeyEvent(e) {
			const validCharsRegex = new RegExp(/^[0-9 +\-()]*$/);

			// Allow Control+V or Command+V for pasting
			const isPasteOperation = (e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey);

			if(!validCharsRegex.test(e.key) && ![
				'ArrowLeft',
				'ArrowRight',
				'Backspace',
				'Delete',
				'Tab'
			].includes(e.key) && !isPasteOperation) {
				e.preventDefault();
			}
		},
	},
	async mounted() {

		const self = this;
		// Ironclad field
		setTimeout(async () => { // _ps needs time to load
			const { data: agreement } = await $api.agreements.getAgreement(this.ironCladAgreement);
			if(agreement) {
				const group = await IronClad.groups.getGroup(agreement.external_id, this.user.email, { container_selector: 'IronClad' });
				if(group) {
					group.render();

					// TODO:: move this logic to the IronClad api. Kind of hacky
					// _ps is a global js object
					_ps.on('valid', function (params, context) {
						if(params.group === group.parameters?.data?.values[':group'])
							self.userTos = "agreed";
					});

					_ps.on("all", function (event, el, context) {
						if(event === 'unchecked')
							self.userTos = "";
						else if(event === 'checked') // on valid not being called on second click.
							self.userTos = "agreed"
					});

					// not being called for some reason
					_ps.on('invalid', function (params, context) {
						if(params.group === group.parameters?.data?.values[':group'])
							self.userTos = "";
					});

					_ps(agreement.external_id + ":retrieveHTML", {
						fullName: this.user.name,
						userId: this.user.id,
						agreementId: agreement.id,
						agreementAssignmentId: this.user.id
					});
				}
			}
		}, 750);
	},
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables'; // breakpoints

.cursor {
	cursor: pointer;
}

.page {
	position: relative;
}

.background {
	background-image: url('/images/portals/welcome-alert-bg.jpg');
	background-position: center center;
	background-size: cover;
	bottom: 0;
	left: 0;
	position: fixed;
	right: 0;
	top: 0;
	transform: rotate(180deg) scaleX(-1);
	z-index: 1;

	@media #{map-get($display-breakpoints, 'xs-only')} {
		transform: scaleY(-1);
	}
}

.content {
	min-height: 100vh;
	min-width: 100vw;
	position: relative;
	z-index: 2;
}

.info-container {
}

.info-inner {
	backdrop-filter: blur(30px);
	background: rgba(255, 255, 255, 0.1);
	border-top-left-radius: map-get($rounded, 'xl');
	border-bottom-left-radius: map-get($rounded, 'xl');
	box-shadow: 0 12px 76px 3px rgba(47, 68, 104, 0.28),
	0 5px 92px 8px rgba(47, 68, 104, 0.24), 0 6px 30px 0 rgba(47, 68, 104, 0.4); //rgb(0 67 133)
	height: calc(100% - 10vh);
	margin: 5vh 0 5vh 5vw;
	min-height: 90vh;

	@media #{map-get($display-breakpoints, 'xs-only')} {
		border-bottom-left-radius: 0;
		border-top-right-radius: map-get($rounded, 'xl');
		height: auto;
		margin: 5vh 5vw 0 5vw;
		min-height: calc(60px + 2rem);
	}
}

.form-container {
	background-color: rgba(255, 255, 255, 0.6);
	z-index: 1;
}

.form-inner {
	background: rgba(255, 255, 255, 0.9);
	border-top-right-radius: map-get($rounded, 'xl');
	border-bottom-right-radius: map-get($rounded, 'xl');
	box-shadow: 0 12px 76px 3px rgba(100, 136, 170, 0.14),
	0 5px 92px 8px rgba(100, 136, 170, 0.12), 0 6px 30px 0 rgba(100, 136, 170, 0.2); //rgb(0 67 133)
	height: calc(100% - 10vh);
	margin: 5vh 5vw 5vh 0;
	min-height: 90vh;

	@media #{map-get($display-breakpoints, 'xs-only')} {
		border-bottom-left-radius: map-get($rounded, 'xl');
		border-top-right-radius: 0;
		height: auto;
		margin: 0 5vw 5vh 5vw;
		min-height: calc(90vh - 60px - 2rem);
	}
}

#IronClad {
	min-height: 3rem;
}

.ironclad-checkbox {
	display: none !important;
}
</style>
